import { useMemo } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Lightbulb,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { getSourcesForModule } from "../content/course";
import type { CourseModule } from "../content/types";
import { isActivityReady } from "../lib/activity";
import type { ActivityState } from "../lib/progress";
import { ActivityPanel } from "./Activities";
import { LessonMedia } from "./LessonMedia";

type ModulePageProps = {
  module: CourseModule;
  activityState?: ActivityState;
  isCompleted: boolean;
  canComplete: boolean;
  onActivityChange: (state: ActivityState, ready: boolean) => void;
  onComplete: () => void;
  onBack?: () => void;
  finalModule: boolean;
};

const calloutIcons = {
  insight: Sparkles,
  warning: ShieldAlert,
  practice: Lightbulb,
};

function renderText(text: string): React.ReactNode {
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|#[a-zA-Z][\w-]*)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s<]+)/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    if (match[1] && match[2]) {
      const internal = match[2].startsWith("#");
      nodes.push(<a key={`${match[1]}-${index}`} href={match[2]} target={internal ? undefined : "_blank"} rel={internal ? undefined : "noreferrer"}>{match[1]}</a>);
    } else if (match[3]) {
      nodes.push(<strong key={`${match[3]}-${index}`}>{match[3]}</strong>);
    } else if (match[4]) {
      nodes.push(<a key={`${match[4]}-${index}`} href={match[4]} target="_blank" rel="noreferrer">{match[4]}</a>);
    }
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function ModulePage({
  module,
  activityState,
  isCompleted,
  canComplete,
  onActivityChange,
  onComplete,
  onBack,
  finalModule,
}: ModulePageProps) {
  const ready = useMemo(
    () => isActivityReady(module, activityState),
    [activityState, module],
  );
  const sources = useMemo(() => getSourcesForModule(module.id), [module.id]);

  return (
    <article className="lesson">
      <header className="lesson-header">
        <div>
          <p className="eyebrow">{module.eyebrow}</p>
          <h1>{module.title}</h1>
          <p>{module.description}</p>
        </div>
        <span className="duration">
          <Clock3 size={17} />
          {module.durationMinutes} min
        </span>
      </header>

      {module.videos?.length || module.narration.audioSrc ? (
        <div className="lesson-media-list">
          {module.narration.audioSrc ? (
            <LessonMedia
              key={`${module.id}-narration`}
              title={module.title}
              transcript={module.narration.audioTranscript ?? module.narration.script}
              narrationAudioSrc={module.narration.audioSrc}
              narrationLabel={module.narration.audioLabel}
            />
          ) : null}
          {(module.videos ?? []).map((video) => (
            <LessonMedia key={video.title} title={video.title} transcript={module.narration.script} video={video} />
          ))}
        </div>
      ) : null}

      <div className="lesson-sections">
        {module.sections.map((section) => {
          const CalloutIcon = section.callout
            ? calloutIcons[section.callout.tone]
            : null;
          return (
            <section className="lesson-section" id={section.id} key={section.heading}>
              {section.groupHeading ? <h2>{section.groupHeading}</h2> : null}
              {section.eyebrow ? (
                <p className="eyebrow">{section.eyebrow}</p>
              ) : null}
              {section.headingLevel === 3 ? <h3>{section.heading}</h3> : <h2>{section.heading}</h2>}
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{renderText(paragraph)}</p>
              ))}
              {section.bullets ? (
                <ul className="lesson-bullets">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>
                      <CheckCircle2 size={18} />
                      <span>{renderText(bullet)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {section.steps ? (
                <ol className="lesson-steps">{section.steps.map((step) => (
                  typeof step === "string" ? <li key={step}>{renderText(step)}</li> : (
                    <li key={step.text}>
                      <p>{renderText(step.text)}</p>
                      {step.code ? <pre className="lesson-code"><code>{step.code}</code></pre> : null}
                      {step.result ? <p>{renderText(step.result)}</p> : null}
                    </li>
                  )
                ))}</ol>
              ) : null}
              {section.codeBlocks ? (
                <div className="lesson-code-list">
                  {section.codeBlocks.map((code) => <pre className="lesson-code" key={code}><code>{code}</code></pre>)}
                </div>
              ) : null}
              {section.table ? (
                <div className="lesson-table-wrap"><table className="lesson-table">
                  <thead><tr>{section.table.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
                  <tbody>{section.table.rows.map((row) => <tr key={row.join("|")}>{row.map((cell) => <td key={cell}>{renderText(cell)}</td>)}</tr>)}</tbody>
                </table></div>
              ) : null}
              {section.callout && CalloutIcon ? (
                <aside className={`callout callout-${section.callout.tone}`}>
                  <CalloutIcon size={21} />
                  <div>
                    <strong>{section.callout.title}</strong>
                    <p>{renderText(section.callout.body)}</p>
                  </div>
                </aside>
              ) : null}
            </section>
          );
        })}
      </div>

      {sources.length ? (
        <section className="source-panel lesson-sources" aria-labelledby="lesson-sources-heading">
          <div>
            <BookOpen size={22} aria-hidden="true" />
            <p className="eyebrow">Evidence behind the lesson</p>
            <h2 id="lesson-sources-heading">Sources used in this module</h2>
            <p>
              Use these references to verify the concepts, go deeper, or check
              whether guidance has changed since this course was reviewed.
            </p>
          </div>
          <ul>
            {sources.map((source) => (
              <li key={source.id}>
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.title} <ExternalLink size={15} aria-hidden="true" />
                </a>
                <small>Reviewed {source.reviewedAt}</small>
                {source.description ? <p>{source.description}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {module.activity ? (
        <ActivityPanel
          activity={module.activity}
          initialState={(activityState?.primary as ActivityState | undefined) ?? activityState}
          onChange={(state, ready) => onActivityChange({ ...activityState, primary: state }, ready)}
        />
      ) : null}
      {module.knowledgeCheck ? (
        <ActivityPanel
          activity={module.knowledgeCheck}
          initialState={activityState?.knowledge as ActivityState | undefined}
          onChange={(state, ready) => onActivityChange({ ...activityState, knowledge: state }, ready)}
        />
      ) : null}

      <footer className="lesson-footer">
        {onBack ? (
          <button className="secondary-button" type="button" onClick={onBack}>
            <ArrowLeft size={17} /> Previous
          </button>
        ) : (
          <span />
        )}
        <div className="lesson-complete">
          {!ready && !isCompleted ? (
            <span>Complete the activity to continue</span>
          ) : null}
          <button
            className="primary-button"
            type="button"
            onClick={onComplete}
            disabled={!canComplete || (!ready && !isCompleted)}
          >
            {finalModule ? "Continue to Knowledge check" : isCompleted ? "Continue" : `Continue to ${module.id === "orientation" ? "LLM basics" : module.id === "mental-models" ? "LLM risks" : module.id === "safe-ai" ? "Using AI at work" : module.id === "prompt-loop" ? "Workshop vocabulary" : "What exists already"}`}
            <ArrowRight size={17} />
          </button>
          {finalModule ? (
            <small>Next: final knowledge check</small>
          ) : null}
        </div>
      </footer>
    </article>
  );
}
